import React from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import BaseDialog from '../dialogs/BaseDialog';
import CropPortraitOutlinedIcon from '@mui/icons-material/CropPortraitOutlined';
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined';
import Crop169OutlinedIcon from '@mui/icons-material/Crop169Outlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';

export type CardSize = 'standard' | 'square' | 'landscape';
export type CardRadius = 'standard' | 'rounded' | 'sharp';

export const CARD_RADIUS_VALUES: Record<CardRadius, string> = {
  standard: '4px',
  rounded: '12px',
  sharp: '0px',
};

export const isViewDefault = (
  cardSize: CardSize,
  cardRadius: CardRadius,
  uiVisible: boolean,
  titleVisible: boolean,
) =>
  cardSize === 'standard' &&
  cardRadius === 'standard' &&
  uiVisible === true &&
  titleVisible === true;

interface ViewSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  cardSize: CardSize;
  uiVisible: boolean;
  titleVisible: boolean;
  cardRadius: CardRadius;
  onCardSizeChange: (size: CardSize) => void;
  onUiVisibleChange: (visible: boolean) => void;
  onTitleVisibleChange: (visible: boolean) => void;
  onCardRadiusChange: (radius: CardRadius) => void;
  onReset: () => void;
}

const toggleSx = {
  '& .MuiToggleButton-root': {
    py: 1,
    fontSize: '0.7rem',
    color: 'text.secondary',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    '&.Mui-selected': {
      backgroundColor: 'rgba(32, 136, 255, 0.16)',
      color: 'primary.main',
      '&:hover': { backgroundColor: 'rgba(32, 136, 255, 0.24)' },
    },
  },
} as const;

const RadiusPreview: React.FC<{ radius: string }> = ({ radius }) => (
  <Box
    sx={{
      width: 20,
      height: 26,
      border: '1.5px solid currentColor',
      borderRadius: radius,
      opacity: 0.8,
    }}
  />
);

const ViewSettingsDialog: React.FC<ViewSettingsDialogProps> = ({
  open,
  onClose,
  cardSize,
  uiVisible,
  titleVisible,
  cardRadius,
  onCardSizeChange,
  onUiVisibleChange,
  onTitleVisibleChange,
  onCardRadiusChange,
  onReset,
}) => {
  const handleSizeChange = (_: React.MouseEvent<HTMLElement>, v: CardSize | null) => {
    if (v !== null) onCardSizeChange(v);
  };
  const handleRadiusChange = (_: React.MouseEvent<HTMLElement>, v: CardRadius | null) => {
    if (v !== null) onCardRadiusChange(v);
  };
  const handleTitleChange = (_: React.MouseEvent<HTMLElement>, v: 'on' | 'off' | null) => {
    if (v !== null) onTitleVisibleChange(v === 'on');
  };
  const handleUiChange = (_: React.MouseEvent<HTMLElement>, v: 'on' | 'off' | null) => {
    if (v !== null) onUiVisibleChange(v === 'on');
  };

  const isDefault = isViewDefault(cardSize, cardRadius, uiVisible, titleVisible);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="表示設定"
      actions={
        <Button onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          閉じる
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* デフォルトに戻す */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
          <Button
            size="small"
            onClick={onReset}
            disabled={isDefault}
            sx={{
              fontSize: '0.7rem',
              color: isDefault ? 'text.disabled' : 'text.secondary',
              textTransform: 'none',
              p: '2px 6px',
              minWidth: 0,
            }}
          >
            デフォルトに戻す
          </Button>
        </Box>

        {/* 画像サイズ */}
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
            画像サイズ
          </Typography>
          <ToggleButtonGroup value={cardSize} exclusive onChange={handleSizeChange} size="small" fullWidth sx={toggleSx}>
            <ToggleButton value="standard">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <CropPortraitOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                <span>標準</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="square">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <CropSquareOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                <span>正方形</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="landscape" disabled sx={{ position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  fontSize: '0.5rem',
                  lineHeight: 1,
                  px: '4px',
                  py: '2px',
                  borderRadius: '3px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'text.disabled',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                }}
              >
                準備中
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Crop169OutlinedIcon sx={{ fontSize: '1.1rem' }} />
                <span>横長</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* 角丸 */}
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
            フレーム
          </Typography>
          <ToggleButtonGroup value={cardRadius} exclusive onChange={handleRadiusChange} size="small" fullWidth sx={toggleSx}>
            <ToggleButton value="standard">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <RadiusPreview radius="3px" />
                <span>標準</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="rounded">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <RadiusPreview radius="8px" />
                <span>角丸</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="sharp">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <RadiusPreview radius="0px" />
                <span>角</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* タイトル */}
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
            タイトル
          </Typography>
          <ToggleButtonGroup value={titleVisible ? 'on' : 'off'} exclusive onChange={handleTitleChange} size="small" fullWidth sx={toggleSx}>
            <ToggleButton value="on">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TitleOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                <span>表示</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="off">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VisibilityOffOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                <span>非表示</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* UI 表示 */}
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
            UI 表示（スコア・ステータス・アイコン等）
          </Typography>
          <ToggleButtonGroup value={uiVisible ? 'on' : 'off'} exclusive onChange={handleUiChange} size="small" fullWidth sx={toggleSx}>
            <ToggleButton value="on">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VisibilityOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                <span>表示</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="off">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VisibilityOffOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                <span>非表示</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
    </BaseDialog>
  );
};

export default ViewSettingsDialog;
